"use client"

import { CardDescription } from "@/components/ui/card"

import { useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { CircleNotch as Loader2, Plus, ArrowCounterClockwise as RotateCcw } from "@phosphor-icons/react"
import { apiRequestWithAuth, getStoredToken } from "@/lib/api-config"

interface CDK {
  id: number
  cdk: string
  type: "daily" | "rouge" | "sand_fire"
  param: number
  tag: string
  used: 0 | 1
}

interface CDKListResponse {
  code: number
  msg: string
  data: {
    cdkList: CDK[]
  }
}

interface CreateCDKPayload {
  type: "daily" | "rouge" | "sand_fire"
  param: number
  tag: string
  count: number
}

export default function CdkManagementPage() {
  const { toast } = useToast()

  const [allCDKs, setAllCDKs] = useState<CDK[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [searchType, setSearchType] = useState<string>("daily")
  const [searchTag, setSearchTag] = useState<string>("")
  const [searchCdkString, setSearchCdkString] = useState<string>("")
  const [filterUsed, setFilterUsed] = useState<string>("all")
  const [filterPreset, setFilterPreset] = useState<string>("")
  const [filterCustomDays, setFilterCustomDays] = useState<string>("")

  // 筛选时长套餐配置（与创建表单对称）
  const FILTER_PRESETS: Record<string, { label: string; days: number }> = {
    day7: { label: "7 天体验卡", days: 7 },
    month30: { label: "月卡（30 天）", days: 30 },
    season90: { label: "季卡（90 天）", days: 90 },
    year365: { label: "年卡（365 天）", days: 365 },
  }

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const [isAddCDKDialogOpen, setIsAddCDKDialogOpen] = useState(false)
  const [newCdkType, setNewCdkType] = useState<"daily" | "rouge" | "sand_fire">("daily")
  const [newCdkPreset, setNewCdkPreset] = useState<string>("month30")
  const [newCdkCustomAmount, setNewCdkCustomAmount] = useState<number>(1)

  // 时长套餐配置
  const PRESETS: Record<string, { label: string; days: number | null }> = {
    day7: { label: "7 天体验卡 (7 天)", days: 7 },
    month30: { label: "月卡 (30 天)", days: 30 },
    season90: { label: "季卡 (90 天)", days: 90 },
    year365: { label: "年卡 (365 天)", days: 365 },
    day: { label: "自定义天数", days: null },
    month: { label: "自定义月数 (×30)", days: null },
    year: { label: "自定义年数 (×365)", days: null },
  }

  function computeDays(): number {
    const preset = PRESETS[newCdkPreset]
    if (preset.days !== null) return preset.days
    const n = newCdkCustomAmount || 0
    if (newCdkPreset === "day") return n
    if (newCdkPreset === "month") return n * 30
    return n * 365
  }
  const [newCdkTag, setNewCdkTag] = useState("")
  const [newCdkCount, setNewCdkCount] = useState<number>(1)
  const [creatingCdk, setCreatingCdk] = useState(false)

  const fetchCDKs = async () => {
    setLoading(true)
    setError(null)
    const token = getStoredToken()
    if (!token) {
      setError("未找到认证令牌，请重新登录。")
      setLoading(false)
      return
    }

    try {
      let endpoint: string
      if (searchType && searchType !== "all") {
        endpoint = `/checkCDKByType?keyword=${searchType}`
      } else if (searchTag) {
        endpoint = `/checkCDKByTag?keyword=${searchTag}`
      } else {
        endpoint = `/checkCDKByType?keyword=`
      }

      const result: CDKListResponse = await apiRequestWithAuth(endpoint, token)

      if (result.code === 200 && Array.isArray(result.data?.cdkList)) {
        setAllCDKs(result.data.cdkList)
      } else {
        setAllCDKs([])
        setError(result.msg || "获取CDK失败：数据格式无效。")
      }
    } catch (err: any) {
      setError(`获取CDK失败。${err.message || "请重试。"}`)
      setAllCDKs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCDKs()
  }, [searchType, searchTag])

  const filteredCDKs = useMemo(() => {
    let tempCDKs = allCDKs
    if (searchCdkString) {
      tempCDKs = tempCDKs.filter((cdk) => cdk.cdk.toLowerCase().includes(searchCdkString.toLowerCase()))
    }
    if (filterUsed !== "all") {
      const usedValue = Number.parseInt(filterUsed)
      tempCDKs = tempCDKs.filter((cdk) => cdk.used === usedValue)
    }
    if (filterPreset === "custom" && filterCustomDays) {
      const v = Number.parseInt(filterCustomDays)
      if (!isNaN(v)) {
        tempCDKs = tempCDKs.filter((cdk) => cdk.param === v)
      }
    } else if (filterPreset && FILTER_PRESETS[filterPreset]) {
      const targetDays = FILTER_PRESETS[filterPreset].days
      tempCDKs = tempCDKs.filter((cdk) => cdk.param === targetDays)
    }

    return tempCDKs
  }, [allCDKs, searchCdkString, filterUsed, filterPreset, filterCustomDays])

  const totalPages = Math.ceil(filteredCDKs.length / itemsPerPage)
  const paginatedCDKs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredCDKs.slice(startIndex, endIndex)
  }, [filteredCDKs, currentPage, itemsPerPage])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleResetFilters = () => {
    setSearchType("daily")
    setSearchTag("")
    setSearchCdkString("")
    setFilterUsed("all")
    setFilterPreset("")
    setFilterCustomDays("")
    setCurrentPage(1)
    fetchCDKs()
  }

  const handleCreateCDK = async () => {
    setCreatingCdk(true)
    const token = getStoredToken()
    if (!token) {
      toast({
        title: "创建失败",
        description: "未找到认证令牌，请重新登录。",
        variant: "destructive",
      })
      setCreatingCdk(false)
      return
    }

    try {
      // 提交参数：daily 类型用时长套餐天数，其他类型用参数
      const paramValue = newCdkType === 'daily' ? computeDays() : newCdkCustomAmount
      const payload: CreateCDKPayload = {
        type: newCdkType,
        param: paramValue,
        tag: newCdkTag,
        count: newCdkCount,
      }

      const result = await apiRequestWithAuth("/createCDK", token, {
        method: "POST",
        body: JSON.stringify(payload),
      })

      if (result.code === 200) {
        toast({
          title: "CDK创建成功",
          description: result.msg || `成功创建 ${newCdkCount} 个CDK。`,
        })
        setIsAddCDKDialogOpen(false)
        setNewCdkType("daily")
        setNewCdkPreset("month30")
        setNewCdkCustomAmount(1)
        setNewCdkTag("")
        setNewCdkCount(1)
        await fetchCDKs()
      } else {
        toast({
          title: "CDK创建失败",
          description: result.msg || "创建CDK时发生错误。",
          variant: "destructive",
        })
      }
    } catch (err: any) {
      toast({
        title: "CDK创建失败",
        description: `网络错误或服务器无响应。${err.message || ""}`,
        variant: "destructive",
      })
    } finally {
      setCreatingCdk(false)
    }
  }

  return (
    <>
      <div className="mb-6">
        <div className="page-title-box">
          <div className="page-title">CDK 管理</div>
          <div className="page-subtitle">管理系统中的所有 CDK</div>
        </div>
      </div>

      <Card style={{ border: "1.5px solid var(--berry-400)", background: "radial-gradient(circle, rgba(252,115,109,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,160,155,0.12) 1px, transparent 1px), var(--berry-50)", color: "var(--berry-700)" }} className="mb-6  border-berry-400">
        <CardHeader>
          <CardTitle className="text-berry-700">搜索与筛选</CardTitle>
          <CardDescription className="text-soil-600">根据条件查找 CDK</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="searchType" className="text-berry-700">
                按类型查询
              </Label>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger id="searchType" className=" text-berry-700">
                  <SelectValue placeholder="选择类型" />
                </SelectTrigger>
                <SelectContent className="dark:bg-popover dark:border-popover-foreground">
                  <SelectItem value="daily">日常任务</SelectItem>
                  <SelectItem value="rouge">肉鸽任务</SelectItem>
                  <SelectItem value="sand_fire">生息演算</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="searchTag" className="text-berry-700">
                按标签查询
              </Label>
              <Input
                id="searchTag"
                placeholder="输入标签"
                value={searchTag}
                onChange={(e) => setSearchTag(e.target.value)}
                className=" text-berry-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="searchCdkString" className="text-berry-700">
                本地CDK查询
              </Label>
              <Input
                id="searchCdkString"
                placeholder="输入CDK字符串"
                value={searchCdkString}
                onChange={(e) => setSearchCdkString(e.target.value)}
                className=" text-berry-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filterUsed" className="text-berry-700">
                是否已使用
              </Label>
              <Select value={filterUsed} onValueChange={setFilterUsed}>
                <SelectTrigger id="filterUsed" className=" text-berry-700">
                  <SelectValue placeholder="选择" />
                </SelectTrigger>
                <SelectContent className="dark:bg-popover dark:border-popover-foreground">
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="1">已使用</SelectItem>
                  <SelectItem value="0">未使用</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-berry-700">
                时长筛选
              </Label>
              <Select value={filterPreset} onValueChange={setFilterPreset}>
                <SelectTrigger className=" text-berry-700">
                  <SelectValue placeholder="不限" />
                </SelectTrigger>
                <SelectContent className="dark:bg-popover dark:border-popover-foreground">
                  <SelectItem value="day7">7 天体验卡</SelectItem>
                  <SelectItem value="month30">月卡（30 天）</SelectItem>
                  <SelectItem value="season90">季卡（90 天）</SelectItem>
                  <SelectItem value="year365">年卡（365 天）</SelectItem>
                  <SelectItem value="custom">自定义</SelectItem>
                </SelectContent>
              </Select>
              {filterPreset === "custom" && (
                <Input
                  type="number"
                  min={1}
                  placeholder="输入天数"
                  value={filterCustomDays}
                  onChange={(e) => setFilterCustomDays(e.target.value)}
                  className=" text-berry-700"
                />
              )}
              <p className="text-xs text-gray-500 text-soil-600">
                按天数精确匹配：选预设套餐查 7/30/90/365 天 CDK；选自定义输入天数查。
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={handleResetFilters} variant="outline" disabled={loading}>
              <RotateCcw className="mr-2 h-4 w-4" />
              重置筛选
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card style={{ border: "1.5px solid var(--berry-400)", background: "radial-gradient(circle, rgba(252,115,109,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(255,160,155,0.12) 1px, transparent 1px), var(--berry-50)", color: "var(--berry-700)" }} className=" border-berry-400">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-2xl font-bold tracking-tight text-berry-700">CDK 列表</CardTitle>
          <Button onClick={() => setIsAddCDKDialogOpen(true)} disabled={loading}>
            <Plus className="mr-2 h-4 w-4" />
            创建 CDK
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500 text-soil-600">加载中...</span>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 h-40 flex items-center justify-center">{error}</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-berry-400">
                    <TableHead className="text-soil-600">ID</TableHead>
                    <TableHead className="text-soil-600">CDK</TableHead>
                    <TableHead className="text-soil-600">类型</TableHead>
                    <TableHead className="text-soil-600">时长 / 参数</TableHead>
                    <TableHead className="text-soil-600">标签</TableHead>
                    <TableHead className="text-soil-600">是否已使用</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCDKs.length > 0 ? (
                    paginatedCDKs.map((cdk) => (
                      <TableRow
                        key={cdk.id}
                        className="border-berry-400 hover:bg-gray-50 hover:bg-berry-50"
                      >
                        <TableCell className="text-berry-700">{cdk.id}</TableCell>
                        <TableCell className="text-berry-700">{cdk.cdk}</TableCell>
                        <TableCell className="text-berry-700">{cdk.type}</TableCell>
                        <TableCell className="text-berry-700">
                          {cdk.type === 'daily'
                            ? `${cdk.param} 天 (${(cdk.param / 30).toFixed(1)} 个月)`
                            : cdk.param}
                        </TableCell>
                        <TableCell className="text-berry-700">{cdk.tag}</TableCell>
                        <TableCell className="text-berry-700">{cdk.used === 1 ? "是" : "否"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 text-soil-600">
                        无 CDK 数据。
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {filteredCDKs.length > itemsPerPage && (
                <div className="responsive-pagination-controls flex justify-end items-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="border-berry-400 text-berry-700 hover:bg-berry-50"
                  >
                    上一页
                  </Button>
                  <span aria-live="polite">第 {currentPage} / {totalPages} 页</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="border-berry-400 text-berry-700 hover:bg-berry-50"
                  >
                    下一页
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Dialog open={isAddCDKDialogOpen} onOpenChange={setIsAddCDKDialogOpen}>
        <DialogContent className="sm:max-w-[425px]  border-berry-400" style={{ background: "var(--berry-50)" }}>
          <DialogHeader>
            <DialogTitle className="text-berry-700">创建新 CDK</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newCdkType" className="text-right text-berry-700">
                类型
              </Label>
              <Select
                value={newCdkType}
                onValueChange={(value) => setNewCdkType(value as "daily" | "rouge" | "sand_fire")}
              >
                <SelectTrigger className="col-span-3  text-berry-700">
                  <SelectValue placeholder="选择类型" />
                </SelectTrigger>
                <SelectContent className="dark:bg-popover dark:border-popover-foreground">
                  <SelectItem value="daily">日常任务</SelectItem>
                  <SelectItem value="rouge">肉鸽任务</SelectItem>
                  <SelectItem value="sand_fire">生息演算</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2 text-berry-700">
                {newCdkType === 'daily' ? '时长套餐' : '参数'}
              </Label>
              <div className="col-span-3 space-y-2">
                {newCdkType === 'daily' ? (
                  <>
                    <Select value={newCdkPreset} onValueChange={setNewCdkPreset}>
                      <SelectTrigger className=" text-berry-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-popover dark:border-popover-foreground">
                        <SelectItem value="day7">7 天体验卡</SelectItem>
                        <SelectItem value="month30">月卡（30 天）</SelectItem>
                        <SelectItem value="season90">季卡（90 天）</SelectItem>
                        <SelectItem value="year365">年卡（365 天）</SelectItem>
                        <SelectItem value="day">自定义（天）</SelectItem>
                        <SelectItem value="month">自定义（月 ×30）</SelectItem>
                        <SelectItem value="year">自定义（年 ×365）</SelectItem>
                      </SelectContent>
                    </Select>
                    {PRESETS[newCdkPreset].days === null && (
                      <Input
                        type="number"
                        min={1}
                        value={newCdkCustomAmount}
                        onChange={(e) => setNewCdkCustomAmount(Number.parseInt(e.target.value) || 0)}
                        className=" text-berry-700"
                        placeholder="输入数量"
                      />
                    )}
                    <p className="text-xs text-gray-500 text-soil-600">
                      将生成 <span className="font-semibold text-cyan-400">{computeDays()} 天</span> 的日常 CDK
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      type="number"
                      value={newCdkCustomAmount}
                      onChange={(e) => setNewCdkCustomAmount(Number.parseInt(e.target.value) || 0)}
                      className=" text-berry-700"
                    />
                    <p className="text-xs text-gray-500 text-soil-600">
                      肉鸽/生息 CDK 参数（如关卡号等）
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newCdkTag" className="text-right text-berry-700">
                标签
              </Label>
              <Input
                id="newCdkTag"
                value={newCdkTag}
                onChange={(e) => setNewCdkTag(e.target.value)}
                className="col-span-3  text-berry-700"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newCdkCount" className="text-right text-berry-700">
                生成数量
              </Label>
              <Input
                id="newCdkCount"
                type="number"
                value={newCdkCount}
                onChange={(e) => setNewCdkCount(Number.parseInt(e.target.value) || 1)}
                min={1}
                className="col-span-3  text-berry-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setIsAddCDKDialogOpen(false)}
              variant="outline"
              className="border-berry-400 text-berry-700 hover:bg-berry-50"
            >
              取消
            </Button>
            <Button onClick={handleCreateCDK} disabled={creatingCdk}>
              {creatingCdk && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
